import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Chip,
  Button,
  TextField,
  MenuItem,
  CircularProgress,
  Divider,
  Paper,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const statusColors = {
  completed: 'success',
  cancelled: 'error',
};

const DeliveryHistory = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('week');
  const [stats, setStats] = useState({
    total_earnings: 0,
    total_deliveries: 0,
    avg_rating: 0,
  });

  const loadHistory = useCallback(async () => {
    try {
      const response = await api.get('/delivery/history', { params: { period: dateFilter } });
      setDeliveries(response.data.deliveries || []);
      if (response.data.stats) setStats(response.data.stats);
    } catch (err) {
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filteredDeliveries = deliveries.filter((delivery) => {
    if (filter === 'all') return true;
    return delivery.type === filter;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      <Box sx={{ bgcolor: 'primary.main', color: 'white', py: 4 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Historique des livraisons
          </Typography>
          <Typography variant="body1">
            Consultez vos performances et vos gains
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ mt: -4 }}>
        {/* Statistiques période */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.total_deliveries}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Livraisons
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <AttachMoneyIcon color="success" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.total_earnings.toLocaleString()}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      € gagnés
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={4}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  <TrendingUpIcon color="warning" sx={{ fontSize: 40 }} />
                  <Box>
                    <Typography variant="h4" fontWeight={700}>
                      {stats.avg_rating}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Note moyenne
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Filtres */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Type de livraison"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="all">Toutes</MenuItem>
                <MenuItem value="restaurant">Restaurant</MenuItem>
                <MenuItem value="logistics">Logistique</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label="Période"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="today">Aujourd'hui</MenuItem>
                <MenuItem value="week">Cette semaine</MenuItem>
                <MenuItem value="month">Ce mois</MenuItem>
                <MenuItem value="all">Tout l'historique</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </Paper>

        {/* Liste des livraisons */}
        {filteredDeliveries.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              Aucune livraison dans cette période
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {filteredDeliveries.map((delivery) => (
              <Grid item xs={12} key={delivery.id}>
                <Card>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                      <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={1}>
                          <Chip
                            label={delivery.type === 'restaurant' ? 'Restaurant' : 'Logistique'}
                            size="small"
                            color="primary"
                          />
                          <Chip
                            label="Terminé"
                            size="small"
                            color={statusColors[delivery.status]}
                          />
                        </Box>
                        <Typography variant="h6" fontWeight={600}>
                          {delivery.restaurant_name || `Colis - ${delivery.customer_name}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Commande #{delivery.order_number}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="h6" color="success.main" fontWeight={700}>
                          +{delivery.earnings} €
                        </Typography>
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <Typography variant="body2" color="warning.main">
                            {'⭐'.repeat(delivery.rating)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Récupération
                        </Typography>
                        <Typography variant="body2">{delivery.pickup_address}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Typography variant="caption" color="text.secondary">
                          Livraison
                        </Typography>
                        <Typography variant="body2">{delivery.delivery_address}</Typography>
                      </Grid>
                    </Grid>

                    <Box display="flex" gap={2} mt={2} alignItems="center">
                      <Chip
                        label={`${delivery.distance} km`}
                        size="small"
                        icon={<AccessTimeIcon />}
                      />
                      <Chip
                        label={delivery.completed_at}
                        size="small"
                        icon={<CalendarTodayIcon />}
                      />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Bouton retour */}
        <Box sx={{ mt: 4 }}>
          <Button
            fullWidth
            variant="outlined"
            size="large"
            onClick={() => navigate('/delivery/dashboard')}
          >
            Retour au tableau de bord
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default DeliveryHistory;
